import React, { useState, useCallback, useRef, useEffect } from 'react';
import { BaseEdge, EdgeProps, getSmoothStepPath, getBezierPath, Position } from 'reactflow';
import type { Edge } from '../types';

// Shared refs - these need to be accessible from both the component and the main page
// They're defined here but will be initialized in the main component
// eslint-disable-next-line react-refresh/only-export-components
export const edgeUpdateModeRef: { [edgeId: string]: 'source' | 'target' } = {};
// eslint-disable-next-line react-refresh/only-export-components
export const onEdgeUpdateStartRef: { current: (() => void) | null } = { current: null };
// eslint-disable-next-line react-refresh/only-export-components
export const onConnectStartRef: { current: ((event: React.MouseEvent | React.TouchEvent, params: { nodeId: string | null; handleType: string | null }) => void) | null } = { current: null };
// eslint-disable-next-line react-refresh/only-export-components
export const edgeDragStateRef: {
  edgeId: string | null;
  updateMode: 'source' | 'target' | null;
  startX: number;
  startY: number;
  handleElement: HTMLElement | null;
  originalHandleVisibility: string;
  originalHandleOpacity: string;
} = {
  edgeId: null,
  updateMode: null,
  startX: 0,
  startY: 0,
  handleElement: null,
  originalHandleVisibility: '',
  originalHandleOpacity: '',
};

// Global ref to access edges and nodes from edge component
// Using an object wrapper so it can be mutated from outside
// eslint-disable-next-line react-refresh/only-export-components
export const globalEdgesRef = { current: [] as Edge[] };

// Global ref for edge dragging state (replaces window property)
// eslint-disable-next-line react-refresh/only-export-components
export const isEdgeDraggingRef = { current: false };

// Global ref for setEdges callback to update edges from edge component
// eslint-disable-next-line react-refresh/only-export-components
export const globalSetEdgesRef = { current: null as ((edges: Edge[] | ((prev: Edge[]) => Edge[])) => void) | null };

// Custom edge component that allows dragging from anywhere along the edge
export const DraggableEdge = React.memo(({ 
  id, 
  sourceX, 
  sourceY, 
  targetX, 
  targetY, 
  sourcePosition, 
  targetPosition, 
  style = {}, 
  markerEnd,
  label,
  labelStyle,
  labelShowBg,
  labelBgStyle,
  selected,
}: EdgeProps) => {
  const edgePathParams = {
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  };

  // Get the edge data
  const edge = globalEdgesRef.current.find(e => e.id === id);
  const edgeType = edge?.type || 'smoothstep';

  let edgePath: string;
  let defaultLabelX: number;
  let defaultLabelY: number;
  [edgePath, defaultLabelX, defaultLabelY] = getSmoothStepPath(edgePathParams);

  // Use custom label position if available in edge data, otherwise use default
  const customLabelPos = edge?.data?.labelPosition;
  const labelX = customLabelPos ? customLabelPos.x : defaultLabelX;
  const labelY = customLabelPos ? customLabelPos.y : defaultLabelY;
  
  // State for dragging label
  const [isDraggingLabel, setIsDraggingLabel] = useState(false);
  
  const findClosestPointOnPath = (x: number, y: number, _path: string): { x: number; y: number; offset: number } => {
    const samples = 100;
    let closestPoint = { x: defaultLabelX, y: defaultLabelY, offset: 0.5 };
    let minDistance = Infinity;
    for (let t = 0; t <= 1; t += 1 / samples) {
      const sampleX = sourceX + (targetX - sourceX) * t;
      const sampleY = sourceY + (targetY - sourceY) * t;
      const dist = Math.sqrt(Math.pow(x - sampleX, 2) + Math.pow(y - sampleY, 2));
      if (dist < minDistance) {
        minDistance = dist;
        closestPoint = { x: sampleX, y: sampleY, offset: t };
      }
    }
    return closestPoint;
  };

  return (
    <g>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          pointerEvents: 'none', // Disable pointer events on BaseEdge so overlay can capture all clicks
        }}
      />
      {/* Edge label - draggable along the path */}
      {label && (
        <g transform={`translate(${labelX}, ${labelY})`}>
          {labelShowBg && (() => {
            const labelText = String(label);
            const textWidth = labelText.length * 7; // Approximate character width
            return (
              <rect
                x={-textWidth / 2 - 6}
                y={-10}
                width={textWidth + 12}
                height={20}
                rx={3}
                fill="white"
                stroke="none"
                style={labelBgStyle}
              />
            );
          })()}
          {/* Invisible larger hit area for dragging label */}
          <rect
            x={-100}
            y={-15}
            width={200}
            height={30}
            fill="transparent"
            style={{
              cursor: 'grab',
              pointerEvents: 'all',
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIsDraggingLabel(true);
              
              const svgElement = e.currentTarget.ownerSVGElement;
              if (!svgElement || !globalSetEdgesRef.current) return;
              
              const handleMouseMove = (moveEvent: MouseEvent) => {
                if (!globalSetEdgesRef.current || !svgElement) return;
                
                const svgPoint = svgElement.createSVGPoint();
                svgPoint.x = moveEvent.clientX;
                svgPoint.y = moveEvent.clientY;
                const ctm = svgElement.getScreenCTM();
                if (!ctm) return;
                
                const invertedCTM = ctm.inverse();
                const svgPointTransformed = svgPoint.matrixTransform(invertedCTM);
                
                // Find closest point on path
                const closestPoint = findClosestPointOnPath(
                  svgPointTransformed.x,
                  svgPointTransformed.y,
                  edgePath
                );
                
                // Update label position
                globalSetEdgesRef.current((prevEdges: Edge[]) => {
                  return prevEdges.map(ed => {
                    if (ed.id === id) {
                      return {
                        ...ed,
                        data: {
                          ...ed.data,
                          labelPosition: { x: closestPoint.x, y: closestPoint.y },
                        },
                      };
                    }
                    return ed;
                  });
                });
              };
              
              const handleMouseUp = () => {
                setIsDraggingLabel(false);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
              };
              
              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
          />
          <text
            x={0}
            y={0}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
              fontSize: '12px',
              fontWeight: 500,
              fill: '#1a192b',
              pointerEvents: 'none',
              userSelect: 'none',
              cursor: isDraggingLabel ? 'grabbing' : 'grab',
              ...labelStyle,
            }}
          >
            {label}
          </text>
        </g>
      )}
    </g>
  );
});

DraggableEdge.displayName = 'DraggableEdge';

